const FILE_INPUT_SELECTOR = 'input[type="file"]';
const FILE_UPLOAD_ROOT_SELECTOR = '[data-file-upload-root]';
const ILLEGAL_JIRA_FILENAME_CHARS = /[\\/"%:$?*]/g;
const CLIPBOARD_CHANGE_SOURCE = 'clipboard';

let lastInteractedFileInput: HTMLInputElement | null = null;

export type ClipboardFileInputChangeDetail = {
    source: typeof CLIPBOARD_CHANGE_SOURCE;
};

declare global {
    interface Window {
        __portalClipboardFilePasteEnabled?: boolean;
    }
}

function isFileInput(element: Element | null): element is HTMLInputElement {
    return element instanceof HTMLInputElement && element.type === 'file';
}

function isUsableFileInput(input: HTMLInputElement | null): input is HTMLInputElement {
    return Boolean(input && input.isConnected && !input.disabled);
}

function getFileExtensionFromType(type: string): string {
    const [, rawSubtype = ''] = type.toLowerCase().split('/', 2);
    if (!rawSubtype) {
        return 'bin';
    }

    const subtype = rawSubtype.split('+')[0] || rawSubtype;

    if (subtype === 'jpeg') {
        return 'jpg';
    }

    return subtype;
}

function makeSafeFilename(base: string, extension: string): string {
    const timestamp = new Date()
        .toISOString()
        .replace(/[:.]/g, '-')
        .replace('T', '_')
        .replace(/Z$/, '');

    return `${base}-${timestamp}.${extension}`.replace(ILLEGAL_JIRA_FILENAME_CHARS, '-');
}

function hasFileExtension(fileName: string): boolean {
    return /\.[^/.\\]+$/.test(fileName);
}

function toJiraSafeClipboardFile(file: File): File {
    const cleanedName = file.name.trim().replace(ILLEGAL_JIRA_FILENAME_CHARS, '-');
    const extension = getFileExtensionFromType(file.type);

    let safeName = cleanedName;
    if (!safeName) {
        const base = file.type.startsWith('image/') ? 'screenshot' : 'attachment';
        safeName = makeSafeFilename(base, extension);
    } else if (!hasFileExtension(safeName)) {
        safeName = `${safeName}.${extension}`;
    }

    if (safeName === file.name) {
        return file;
    }

    return new File([file], safeName, {
        type: file.type,
        lastModified: file.lastModified,
    });
}

export function isClipboardFileInputChange(event: Event): event is CustomEvent<ClipboardFileInputChangeDetail> {
    if (!(event instanceof CustomEvent)) {
        return false;
    }

    return event.detail?.source === CLIPBOARD_CHANGE_SOURCE;
}

function getFileInputFromTarget(target: EventTarget | null): HTMLInputElement | null {
    if (!(target instanceof Element)) {
        return null;
    }

    if (isFileInput(target)) {
        return target;
    }

    const root = target.closest(FILE_UPLOAD_ROOT_SELECTOR);
    if (!root) {
        return null;
    }

    const rootFileInput = root.querySelector<HTMLInputElement>(FILE_INPUT_SELECTOR);
    return rootFileInput ?? null;
}

function getClipboardFiles(event: ClipboardEvent): File[] {
    const clipboardData = event.clipboardData;
    if (!clipboardData) {
        return [];
    }

    const files: File[] = [];

    if (clipboardData.items && clipboardData.items.length > 0) {
        for (const item of Array.from(clipboardData.items)) {
            if (item.kind !== 'file') {
                continue;
            }

            const file = item.getAsFile();
            if (file) {
                files.push(toJiraSafeClipboardFile(file));
            }
        }
    }

    if (files.length === 0 && clipboardData.files && clipboardData.files.length > 0) {
        files.push(...Array.from(clipboardData.files).map(toJiraSafeClipboardFile));
    }

    return files;
}

function resolvePasteTarget(event: ClipboardEvent): HTMLInputElement | null {
    const fromEventTarget = getFileInputFromTarget(event.target);
    if (isUsableFileInput(fromEventTarget)) {
        return fromEventTarget;
    }

    const fromActiveElement = getFileInputFromTarget(document.activeElement);
    if (isUsableFileInput(fromActiveElement)) {
        return fromActiveElement;
    }

    if (isUsableFileInput(lastInteractedFileInput)) {
        return lastInteractedFileInput;
    }

    const allFileInputs = Array.from(document.querySelectorAll<HTMLInputElement>(FILE_INPUT_SELECTOR)).filter(
        (input) => !input.disabled && input.isConnected,
    );

    return allFileInputs.length === 1 ? allFileInputs[0] : null;
}

function setInputFiles(input: HTMLInputElement, files: File[]) {
    const clipboardFiles = input.multiple ? files : files.slice(0, 1);
    if (clipboardFiles.length === 0) {
        return false;
    }

    const dataTransfer = new DataTransfer();
    for (const file of clipboardFiles) {
        dataTransfer.items.add(file);
    }

    input.files = dataTransfer.files;
    input.dispatchEvent(
        new CustomEvent<ClipboardFileInputChangeDetail>('change', {
            bubbles: true,
            detail: {
                source: CLIPBOARD_CHANGE_SOURCE,
            },
        }),
    );
    return true;
}

function trackLastInteractedFileInput(event: Event) {
    const input = getFileInputFromTarget(event.target);
    if (isUsableFileInput(input)) {
        lastInteractedFileInput = input;
    }
}

function createPasteHandler() {
    return (event: ClipboardEvent) => {
        if (event.defaultPrevented) {
            return;
        }

        const files = getClipboardFiles(event);
        if (files.length === 0) {
            return;
        }

        const targetInput = resolvePasteTarget(event);
        if (!targetInput) {
            return;
        }

        event.preventDefault();
        const uploaded = setInputFiles(targetInput, files);

        if (!uploaded) {
            return;
        }
    };
}

export function enableClipboardFilePasteForFileInputs() {
    if (typeof window === 'undefined') {
        return;
    }

    if (window.__portalClipboardFilePasteEnabled) {
        return;
    }

    window.__portalClipboardFilePasteEnabled = true;

    document.addEventListener('pointerdown', trackLastInteractedFileInput, true);
    document.addEventListener('focusin', trackLastInteractedFileInput, true);
    document.addEventListener('paste', createPasteHandler());
}
